import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Button, Card, Drawer, Input, Stack, Tabs, Tooltip } from "@salesapp/ui";
import { useNarrowViewport } from "../lib/useNarrowViewport";
import { useAuth } from "../shell/AuthProvider";
import { useAccess } from "../shell/AccessProvider";
import { useOptionalGuide } from "../guide/GuideProvider";
import { HELP_GUIDES, guideVisible, helpHref, type GuideId, type HelpArticle, type HelpGuide, type HelpOpen } from "../help/catalog";
import { helpImage } from "../help/images";
import { helpMemory } from "../help/helpMemory";
import "./pages.css";
import "./help.css";

function isGuideId(value: string | null): value is GuideId {
  return value === "user" || value === "app-admin" || value === "org-admin";
}

function openHref(open: HelpOpen | undefined, orgSlug: string | null | undefined): string | null {
  return helpHref(open, orgSlug);
}

function articleText(item: HelpArticle): string {
  const table = item.table ? [...item.table.columns, ...item.table.rows.flat()] : [];
  return [item.title, item.summary, ...item.steps.map((step) => step.body), ...table].join(" ").toLowerCase();
}

export function HelpPage() {
  const { session } = useAuth();
  const { can } = useAccess();
  const tours = useOptionalGuide();
  const narrow = useNarrowViewport();
  const [topicsOpen, setTopicsOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const canOrgAdmin = Boolean(session?.isOrgAdmin);

  const visibleGuides = useMemo(
    () => HELP_GUIDES.filter((guide) => guideVisible(guide.id, canOrgAdmin, can)),
    [can, canOrgAdmin],
  );

  const requested = params.get("guide");
  const guide: HelpGuide = (isGuideId(requested)
    ? visibleGuides.find((item) => item.id === requested)
    : undefined) ?? visibleGuides[0];

  const allowedArticles = useMemo(
    () => guide.articles.filter((item) => !item.need || can(item.need.capability, item.need.action ?? "read")),
    [can, guide],
  );
  const needle = query.trim().toLowerCase();
  const allowedIn = (item: HelpArticle) => !item.need || can(item.need.capability, item.need.action ?? "read");
  // A search looks through every guide you can see and the full text of each topic
  // (steps and tables too), so "roles" or "users" finds the answer wherever it lives.
  const results: { guide: HelpGuide; article: HelpArticle }[] = needle
    ? visibleGuides.flatMap((entry) => entry.articles
      .filter((item) => allowedIn(item) && articleText(item).includes(needle))
      .map((item) => ({ guide: entry, article: item })))
    : allowedArticles.map((item) => ({ guide, article: item }));
  const groupedResults = visibleGuides
    .map((entry) => ({ guide: entry, rows: results.filter((row) => row.guide.id === entry.id) }))
    .filter((group) => group.rows.length > 0);
  const requestedTopic = params.get("topic");
  const current = results.find((row) => row.guide.id === guide.id && row.article.id === requestedTopic) ?? results[0];
  const activeGuide = current?.guide ?? guide;
  const article: HelpArticle = current?.article ?? allowedArticles[0] ?? guide.articles[0];
  const href = openHref(article.open, session?.orgSlug);

  const paramsText = params.toString();
  useEffect(() => {
    helpMemory.rememberTopic(paramsText ? `?${paramsText}` : "");
  }, [paramsText]);

  function goHelpHome() {
    setParams({}, { replace: true });
    setQuery("");
  }

  function selectGuide(key: string) {
    // While searching, a tab jumps to its first hit and keeps the search.
    const firstHit = needle ? results.find((row) => row.guide.id === key) : undefined;
    if (firstHit) {
      selectTopic(firstHit.article.id, firstHit.guide.id);
      return;
    }
    const next = new URLSearchParams(params);
    next.set("guide", key);
    next.delete("topic");
    setParams(next, { replace: true });
    setQuery("");
  }

  function selectTopic(id: string, guideId: GuideId) {
    const next = new URLSearchParams(params);
    next.set("guide", guideId);
    next.set("topic", id);
    setParams(next, { replace: true });
    setTopicsOpen(false);
    // Each topic starts at the top of the page, not wherever the last one was scrolled to.
    window.scrollTo({ top: 0 });
  }

  const topicNav = (
    <nav className="help-nav" aria-label={`${activeGuide.title} topics`}>
      {results.length === 0 ? (
        <p className="home-subtitle">No topics match that search.</p>
      ) : (
        groupedResults.map((group) => (
          <div key={group.guide.id} className="help-nav-group" role="group" aria-label={group.guide.title}>
            {needle ? (
              <p className={`help-nav-heading help-guide--${group.guide.id}`}>
                {group.guide.title} · {group.rows.length}
              </p>
            ) : null}
            {group.rows.map((row) => (
              <button
                key={`${row.guide.id}-${row.article.id}`}
                type="button"
                className={`help-nav-item${row.article.id === article.id && row.guide.id === activeGuide.id ? " is-active" : ""}`}
                onClick={() => selectTopic(row.article.id, row.guide.id)}
              >
                {row.article.title}
              </button>
            ))}
          </div>
        ))
      )}
    </nav>
  );

  return (
    <Stack gap={5}>
      <div>
        <h1 className="home-title">
          <Tooltip text="Return to help home" label="About Help home">
            <button type="button" className="help-title-btn" onClick={goHelpHome} title="Return to help home">
              Help
            </button>
          </Tooltip>
        </h1>
        <p className="home-subtitle">
          In-app guides for this workspace. They are only available after you sign in.
          You see the guides that match your role.
        </p>
        {tours && tours.available.length > 0 && (
          <div className="help-tour-start">
            <Button type="button" variant="secondary" onClick={() => tours.replay()}>
              Start a guided tour
            </Button>
          </div>
        )}
      </div>
      <Input
        label="Search this guide"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search all guides: company, roles, notifications, billing…"
      />
      <Tabs
        items={visibleGuides.map((item) => ({
          key: item.id,
          label: needle
            ? `${item.title} (${results.filter((row) => row.guide.id === item.id).length})`
            : item.title,
        }))}
        activeKey={activeGuide.id}
        onChange={selectGuide}
      />
      <p className="home-subtitle">
        {needle
          ? `${results.length} ${results.length === 1 ? "topic" : "topics"} found across all guides. The tab counts show where.`
          : activeGuide.blurb}
      </p>
      {narrow ? (
        <div className="help-topics-bar">
          <div>
            <p className="help-nav-heading">Topic</p>
            <strong>{article.title}</strong>
          </div>
          <button
            type="button"
            className="help-topics-menu"
            aria-label="Open topics menu"
            aria-expanded={topicsOpen}
            onClick={() => setTopicsOpen(true)}
          >
            Menu
          </button>
        </div>
      ) : null}
      <div className="help-layout">
        {narrow ? null : topicNav}
        <Card className="help-article">
          <Stack gap={4}>
            <div>
              {needle ? (
                <p className={`help-nav-heading help-guide--${activeGuide.id}`}>{activeGuide.title}</p>
              ) : null}
              <h2 className="home-section-title">{article.title}</h2>
              <p className="home-subtitle">{article.summary}</p>
            </div>
            {href && (
              <Link className="help-open" to={href}>{article.openLabel ?? "Open this screen"}</Link>
            )}
            {article.table && (
              <div className="help-table-wrap">
                <table className="help-table">
                  <thead>
                    <tr>
                      {article.table.columns.map((column) => <th key={column} scope="col">{column}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {article.table.rows.map((row) => (
                      <tr key={row[0]}>
                        {row.map((cell, index) => (index === 0
                          ? <th key={cell} scope="row">{cell}</th>
                          : <td key={`${row[0]}-${index}`}>{cell}</td>))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {article.steps.map((step, index) => {
              const src = helpImage(step.image);
              const stepHref = openHref(step.open, session?.orgSlug);
              return (
                <div key={`${article.id}-${index}`} className="help-step">
                  <p>
                    <strong>{index + 1}.</strong> {step.body}
                  </p>
                  {stepHref && (
                    <Link className="help-open" to={stepHref}>{step.openLabel ?? "Open this screen"}</Link>
                  )}
                  {src && (
                    <img
                      className="help-shot"
                      src={src}
                      alt={`${article.title}, step ${index + 1}`}
                    />
                  )}
                </div>
              );
            })}
          </Stack>
        </Card>
      </div>
      {narrow ? (
        <Drawer isOpen={topicsOpen} onClose={() => setTopicsOpen(false)} title="Topics" side="left">
          {topicNav}
        </Drawer>
      ) : null}
    </Stack>
  );
}
